import { Routes, useParam } from "@blitzjs/next"
import { useQuery } from "@blitzjs/rpc"
import { SIcon } from "@superwall/legacy-ui"
import ApplicationIcon from "app/applications/components/ApplicationIcon"
import getOrganization from "app/organizations/queries/getOrganization"
import getProjects from "app/projects/queries/getProjects"
import { trpc } from "app/core/trpcClient"
import Link from "next/link"
import { useRouter } from "next/router"
import { FC, PropsWithChildren, useEffect, useMemo, useRef, useState } from "react"
import tw from "tailwind-styled-components"
import { useApplicationContext } from "../contexts/ApplicationContext"
import { AppSwitcherSection, Loading } from "./Sidebar"

const PlatformContainer = tw.div`
  relative
  flex
  items-stretch
  gap-2
  w-full
  transition-all
`

const PlatformButton = tw.button<{ $disabled?: boolean }>`
  flex
  items-center
  justify-center
  flex-1
  h-9
  rounded-lg
  transition-colors
  duration-200
  group
  relative
  ${({ $disabled }) =>
    $disabled
      ? "opacity-50 hover:opacity-100 transition-opacity text-white/40 cursor-pointer"
      : "text-white/90"}
`

const ProjectSwitcher: FC<PropsWithChildren & { isHovered?: boolean }> = ({ isHovered }) => {
  const { enabled } = useApplicationContext()
  const applicationId = useParam("applicationId", "number")!
  const [organization] = useQuery(getOrganization, { applicationId }, { suspense: false })
  const { data: application } = trpc.blitzMigration.applications.getApplication.useQuery(
    { id: applicationId },
    { suspense: false }
  )
  const organizationId = organization?.id ?? 0

  const { data: applications } = trpc.blitzMigration.applications.getApplications.useQuery(
    { organizationId },
    { enabled: !!organizationId && !!organization, suspense: false }
  )

  const [projects, projectsRequest] = useQuery(
    getProjects,
    { applicationId },
    { enabled: !!applicationId && !!application, suspense: false }
  )

  const [isOpen, setIsOpen] = useState(false)
  const [hoveredPlatform, setHoveredPlatform] = useState<string | null>(null)
  const [lastHoveredPosition, setLastHoveredPosition] = useState<string | null>(null)
  const hoverTimeoutRef = useRef<NodeJS.Timeout>()
  const lastValidPlatformRef = useRef<string | null>(null)

  const router = useRouter()

  // Find current project
  const currentProject = useMemo(() => {
    const project = projects?.find((project) =>
      Object.values(project.projectApplications).some((app) => app?.id === applicationId)
    )

    // If no project found, create a virtual project from the application
    if (!project && application) {
      return {
        id: `virtual-app-${application.id}`,
        name: application.name,
        projectApplications: {
          [application.platform]: application,
          IOS: application.platform === "IOS" ? application : null,
          ANDROID: application.platform === "ANDROID" ? application : null,
          STRIPE: application.platform === "STRIPE" ? application : null,
          WEBAPP: application.platform === "WEBAPP" ? application : null,
        },
      }
    }

    return project
  }, [projects, applicationId, application])

  const platforms = useMemo(
    () =>
      currentProject
        ? {
            IOS: currentProject.projectApplications.IOS,
            ANDROID: currentProject.projectApplications.ANDROID,
            ...(enabled("web_2_app") ? { STRIPE: currentProject.projectApplications.STRIPE } : {}),
            ...(enabled("web_sdk") ? { WEBAPP: currentProject.projectApplications.WEBAPP } : {}),
          }
        : {
            IOS: null,
            ANDROID: null,
          },
    [currentProject, enabled]
  )

  // Sort platforms to show active ones first
  const sortedPlatforms = useMemo(() => {
    if (!platforms) return []
    return Object.entries(platforms)
      .sort(([, a], [, b]) => {
        if (a && !b) return -1
        if (!a && b) return 1
        return 0
      })
      .map(([platform]) => platform)
  }, [platforms])

  // Get platform position for animations
  const getPlatformPosition = (platform: string) => {
    const index = sortedPlatforms.indexOf(platform)
    return index === -1 ? -1 : index
  }

  // Find current platform
  const currentPlatform = useMemo(() => {
    if (!platforms) return null

    // Check if we're on any add page
    if (router.asPath.match(/\/add\/(new|existing|method)/)) {
      const platform = router.query.platform as string
      if (platform) {
        return platform.toUpperCase()
      }
    }

    return Object.entries(platforms).find(([_, app]) => app?.id === applicationId)?.[0]
  }, [platforms, applicationId, router.asPath, router.query.platform])

  useEffect(() => {
    if (currentPlatform) {
      lastValidPlatformRef.current = currentPlatform
    }
  }, [currentPlatform])

  const activePlatformIndex = currentPlatform ? getPlatformPosition(currentPlatform) : -1

  const handleMouseEnter = (platform: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    setHoveredPlatform(platform)
    setLastHoveredPosition(platform)
  }

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    // @ts-ignore
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredPlatform(null)
    }, 150) // 150ms debounce
  }

  const renderProjectItems = useMemo(() => {
    const projectItems = (projects || [])
      .filter((p) => p.id !== currentProject?.id)
      .map((project) => {
        const firstApp = Object.values(project.projectApplications).find((app) => app)
        if (!firstApp) return null

        return (
          <AppSwitcherSection
            key={project.id}
            onClick={() => {
              router.push(Routes.ShowApplicationDashboardPage({ applicationId: firstApp.id }))
              setIsOpen(false)
            }}
            className="cursor-pointer hover:bg-white/5"
          >
            <ProjectListItem
              name={project.name}
              orgName={organization?.name}
              application={firstApp}
            />
          </AppSwitcherSection>
        )
      })

    const standaloneApps = (applications || [])
      .filter(
        (app) =>
          app.id !== applicationId &&
          !(projects || []).some((project) =>
            Object.values(project.projectApplications).some((pApp) => pApp?.id === app.id)
          )
      )
      .map((app) => (
        <AppSwitcherSection
          key={`virtual-app-${app.id}`}
          onClick={() => {
            router.push(Routes.ShowApplicationDashboardPage({ applicationId: app.id }))
            setIsOpen(false)
          }}
          className="cursor-pointer hover:bg-white/5"
        >
          <ProjectListItem
            name={app.name}
            orgName={organization?.name}
            application={app}
            isVirtualApp={true}
          />
        </AppSwitcherSection>
      ))

    return [...projectItems, ...standaloneApps]
  }, [projects, applications, currentProject?.id, organization?.name, router, applicationId])

  return (
    <div className="w-full">
      {/* Current Project Section */}
      {currentProject ? (
        <AppSwitcherSection
          onClick={() => setIsOpen(!isOpen)}
          className="cursor-pointer hover:bg-grayscale-dark hover:bg-opacity-50 hover:backdrop-blur-xl relative sticky top-0 bg-grayscale-darker z-10"
        >
          <div className="flex items-center gap-3">
            <ApplicationIcon
              application={application}
              showPlatform={
                typeof currentProject.id === "string" &&
                currentProject.id.startsWith("virtual-app-")
              }
            />
            <div className={!isHovered ? "opacity-100 sm:opacity-0 sm:lg:opacity-100" : ""}>
              <h2 className="text-base truncate sm:max-w-[177px]">
                {currentProject?.name || "No Project"}
              </h2>
              <h2 className="text-xs opacity-50 truncate max-w-[200px]">
                {organization?.name || ""}
              </h2>
            </div>
            <span
              className={`ml-auto absolute z-0 right-2 ${
                !isHovered ? "opacity-40 sm:opacity-0 sm:lg:opacity-40" : "opacity-40"
              }`}
            >
              <div className={`${isOpen ? "" : "scale-y-[-1]"} transition-all duration-200`}>
                {OpenAppSwitcherIcon}
              </div>
            </span>
          </div>
        </AppSwitcherSection>
      ) : (
        projectsRequest.isLoading && (
          <div className="w-full">
            {/* App Switcher Skeleton */}
            <div className="p-4 w-full flex items-center gap-4 border-b-[1px] border-white border-opacity-7 min-h-[72px]">
              {/* App Icon Skeleton */}
              <div className="w-10 h-10 rounded-[10px] bg-white/7  shrink-0" />
              {/* Text Skeleton */}
              <div
                className={`flex ${isHovered ? "" : "sm:hidden"} lg:flex flex-col gap-1.5 w-full`}
              >
                <div className="h-4 w-3/4 bg-white/7 rounded " />
                <div className="h-3 w-1/2 bg-white/7 rounded " />
              </div>
            </div>
            {/* Platform Buttons Skeleton */}
            <div className="p-2 w-full flex items-center gap-2 border-b-[1px] border-white border-opacity-7">
              <div className="flex-1 h-9 flex items-center justify-center rounded-full bg-white/7 " />
              <div
                className={`flex-1 ${isHovered ? "" : "sm:hidden"} lg:flex h-9 flex items-center justify-center rounded-full bg-white/7 `}
              />
              <div
                className={`flex-1 ${isHovered ? "" : "sm:hidden"} lg:flex h-9 flex items-center justify-center rounded-full bg-white/7 `}
              />
            </div>
          </div>
        )
      )}

      {/* Project Switcher */}
      {isOpen && (
        <div
          className={`bg-grayscale-darkest ${!isHovered ? "block sm:hidden lg:block" : "block"}`}
        >
          {renderProjectItems}

          <Link href={Routes.CreateProjectPage({ applicationId, platform: "ios" })} passHref>
            <AppSwitcherSection>
              <div className="flex items-center justify-center gap-1">
                <p className="text-2xl w-[40px] text-brand-primary flex items-center justify-center">
                  +
                </p>
                <p className="">{`New Project`}</p>
              </div>
            </AppSwitcherSection>
          </Link>
        </div>
      )}

      {/* Platform Buttons */}
      {currentProject && (
        <AppSwitcherSection className="!min-h-fit !p-2 !pr-2 ">
          <PlatformContainer
            data-active-position={currentPlatform ? getPlatformPosition(currentPlatform) : -1}
          >
            {activePlatformIndex >= 0 && sortedPlatforms.length > 0 && (
              <div
                className={`absolute bottom-0 h-full rounded-2xl border border-white/10 bg-white/10 transition-all duration-300 ease-out ${
                  !isHovered ? "sm:hidden lg:block" : ""
                }`}
                style={{
                  width: `calc((100% - ${(sortedPlatforms.length - 1) * 8}px) / ${
                    sortedPlatforms.length
                  })`,
                  transform: `translateX(calc(${activePlatformIndex} * (100% + 8px)))`,
                }}
              />
            )}
            {sortedPlatforms.map((platform) => (
              <PlatformButton
                key={platform}
                className={`${
                  !isHovered && currentPlatform && currentPlatform !== platform
                    ? "flex sm:hidden lg:flex"
                    : "flex"
                } !min-w-9 shrink-0 ${currentPlatform && currentPlatform !== platform ? "" : ""}`}
                $disabled={!currentProject || !platforms[platform]}
                onMouseEnter={() => handleMouseEnter(platform)}
                onMouseLeave={handleMouseLeave}
                onClick={() => {
                  if (!currentProject) return
                  if (platforms[platform]) {
                    // Extract the current path to maintain navigation context
                    const currentPath = router.asPath
                    const currentAppIdMatch = currentPath.match(/\/applications\/\d+\/([^\/]+)/)
                    const firstSegmentAfterAppId = currentAppIdMatch ? currentAppIdMatch[1] : null

                    // If we have a path segment after the app ID, preserve it when switching platforms
                    if (firstSegmentAfterAppId) {
                      if (firstSegmentAfterAppId === "settings") {
                        router.push(
                          `/applications/${platforms[platform].id}/settings/${
                            currentPath.split("/settings")[1]
                          }`
                        )
                      } else if (firstSegmentAfterAppId === "products") {
                        router.push(
                          `/applications/${platforms[platform].id}/products/${
                            currentPath.split("/products")[1]
                          }`
                        )
                      } else if (firstSegmentAfterAppId === "users") {
                        // Preserve the full users path (e.g., users/v2, users/v2/[appUserId])
                        router.push(
                          `/applications/${platforms[platform].id}/users/${
                            currentPath.split("/users")[1]
                          }`
                        )
                      } else {
                        // TODO: Remove this once we have a proper quickstart page
                        if (platform === "STRIPE" && firstSegmentAfterAppId === "quickstart") {
                          router.push(`/applications/${platforms[platform].id}`)
                          return
                        }

                        if (firstSegmentAfterAppId === "add") {
                          router.push(`/applications/${platforms[platform].id}/`)
                          return
                        }

                        router.push(
                          `/applications/${platforms[platform].id}/${firstSegmentAfterAppId}`
                        )
                      }
                    } else {
                      router.push(
                        Routes.ShowApplicationDashboardPage({
                          applicationId: platforms[platform].id,
                        })
                      )
                    }
                  } else {
                    // Check if current project is virtual
                    const isCurrentVirtual =
                      typeof currentProject?.id === "string" &&
                      currentProject?.id.startsWith("virtual-app-")

                    // Get standalone apps (virtual projects)
                    const standaloneApps = (applications || []).filter(
                      (app) =>
                        app.id !== applicationId &&
                        app.platform === platform &&
                        !(projects || []).some((project) =>
                          Object.values(project.projectApplications).some(
                            (pApp) => pApp?.id === app.id
                          )
                        )
                    )

                    // Determine if we should use AddNewApplicationPage
                    const shouldUseNewPage =
                      standaloneApps.length === 0 ||
                      (isCurrentVirtual && standaloneApps.length === 0)

                    if (shouldUseNewPage) {
                      router.push(
                        Routes.AddNewApplicationPage({
                          applicationId,
                          platform: platform.toLowerCase(),
                          ...(platform === "STRIPE" && application?.name
                            ? {
                                name: application?.name,
                              }
                            : {}),
                        })
                      )
                    } else {
                      router.push(
                        Routes.AddApplicationPageMethod({
                          applicationId,
                          platform: platform.toLowerCase(),
                          projectId: currentProject?.id,
                          isVirtualApp: isCurrentVirtual,
                        })
                      )
                    }
                  }
                }}
              >
                {currentProject && !platforms[platform] ? (
                  <>
                    {platform === "IOS" && (
                      <>
                        <SIcon
                          type="apple"
                          size={18}
                          className="group-hover:opacity-0 group-hover:scale-0 grayscale duration-100 transition-all absolute"
                        />
                        <SIcon
                          type="plus-circle"
                          size={18}
                          className="opacity-0 duration-100 scale-0 group-hover:opacity-100 group-hover:scale-100 text-brand-primary transition-all"
                        />
                      </>
                    )}
                    {platform === "ANDROID" && (
                      <>
                        <div className="group-hover:opacity-0 group-hover:scale-0 grayscale opacity-60 duration-100 transition-all absolute">
                          <SIcon type="google-play" size={18} />
                        </div>
                        <SIcon
                          type="plus-circle"
                          size={18}
                          className="opacity-0 duration-100 scale-0 group-hover:opacity-100 group-hover:scale-100 text-brand-primary transition-all"
                        />
                      </>
                    )}
                    {platform === "STRIPE" && (
                      <>
                        <SIcon
                          type="globus"
                          size={20}
                          className="group-hover:opacity-0 group-hover:scale-0 grayscale duration-100 transition-all absolute"
                        />
                        <SIcon
                          type="plus-circle"
                          size={18}
                          className="opacity-0 duration-100 scale-0 group-hover:opacity-100 group-hover:scale-100 text-brand-primary transition-all"
                        />
                      </>
                    )}
                    {platform === "WEBAPP" && (
                      <>
                        <SIcon
                          type="code"
                          size={18}
                          className="group-hover:opacity-0 group-hover:scale-0 grayscale duration-100 transition-all absolute"
                        />
                        <SIcon
                          type="plus-circle"
                          size={18}
                          className="opacity-0 duration-100 scale-0 group-hover:opacity-100 group-hover:scale-100 text-brand-primary transition-all"
                        />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {platform === "IOS" && (
                      <SIcon
                        type="apple"
                        size={18}
                        className={
                          platforms.IOS?.id !== applicationId
                            ? "grayscale opacity-80 transition-all duration-200 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-[1.1] group-active:scale-[1.05] group-active:opacity-70"
                            : "transition-all duration-200 group-hover:scale-[1.1] group-active:scale-[1.05] group-active:opacity-70"
                        }
                      />
                    )}
                    {platform === "ANDROID" && (
                      <div
                        className={
                          platforms.ANDROID?.id !== applicationId
                            ? "grayscale opacity-80 transition-all duration-200 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-[1.1] group-active:scale-[1.05] group-active:opacity-70"
                            : "transition-all duration-200 group-hover:scale-[1.1] group-active:scale-[1.05] group-active:opacity-70"
                        }
                      >
                        <SIcon type="google-play" size={18} />
                      </div>
                    )}
                    {platform === "STRIPE" && (
                      <SIcon
                        type="globus"
                        size={20}
                        className={
                          platforms.STRIPE?.id !== applicationId
                            ? "opacity-80 transition-all duration-200 group-hover:opacity-100 group-hover:scale-[1.1] group-active:scale-[1.05] group-active:opacity-70"
                            : "transition-all duration-200 group-hover:scale-[1.1] group-active:scale-[1.05] group-active:opacity-70"
                        }
                      />
                    )}
                    {platform === "WEBAPP" && (
                      <SIcon
                        type="code"
                        size={18}
                        className={
                          platforms.WEBAPP?.id !== applicationId
                            ? "opacity-80 transition-all duration-200 group-hover:opacity-100 group-hover:scale-[1.1] group-active:scale-[1.05] group-active:opacity-70"
                            : "transition-all duration-200 group-hover:scale-[1.1] group-active:scale-[1.05] group-active:opacity-70"
                        }
                      />
                    )}
                  </>
                )}
              </PlatformButton>
            ))}
          </PlatformContainer>
        </AppSwitcherSection>
      )}
    </div>
  )
}

const ProjectListItem = ({ name, orgName, application, isVirtualApp = false }) => (
  <div className="flex items-center gap-3">
    <ApplicationIcon application={application} showPlatform={isVirtualApp} />
    <div>
      <h2 className="text-base truncate max-w-[200px]">{name}</h2>
      <h2 className="text-xs opacity-50 truncate max-w-[200px]">{orgName || ""}</h2>
    </div>
  </div>
)

const CloseAppSwitcherIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <title>caret-sm-up</title>
    <g fill="currentColor">
      <path
        d="M16.707,13.293l-4-4a1,1,0,0,0-1.414,0l-4,4a1,1,0,1,0,1.414,1.414L12,11.414l3.293,3.293a1,1,0,0,0,1.414-1.414Z"
        fill="currentColor"
      ></path>
    </g>
  </svg>
)

const OpenAppSwitcherIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <title>caret-sm-up</title>
    <g fill="currentColor">
      <path
        d="M16.707,13.293l-4-4a1,1,0,0,0-1.414,0l-4,4a1,1,0,1,0,1.414,1.414L12,11.414l3.293,3.293a1,1,0,0,0,1.414-1.414Z"
        fill="currentColor"
      ></path>
    </g>
  </svg>
)

export default ProjectSwitcher
